// Smoke
// Features:
// - CPU-based particle system for external chimney smoke
// - Poisson emission for natural irregularity
// - Physics simulation with wind-like attractor

import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'
import vertexShader from './shaders/smoke/vertex.glsl'
import fragmentShader from './shaders/smoke/fragment.glsl'

const SMOKE_CONFIG = {
    maxParticles: 100,
    emissionRate: 12,
    lifetime: 3,
    startSpeed: 1.1,
    spread: 0.5,
    drag: 0.01,
    spinSpeed: Math.PI / 8,
    clearOnReturn: false,
    position: new THREE.Vector3(-2.1, 7.0, -5.6),
    colourStart: new THREE.Color('#ffffff'),
    colourEnd: new THREE.Color('#ffffff'),
    attractorTarget: new THREE.Vector3(3.0, 8.4, -5.8),
    attractorIntensity: 1.7,
    attractorRadius: 12.2,
    sizeStart: 0.67,
    sizeEnd: 4.28,
    fadeIn: 0.15,
    fadeOutStart: 0.5,
}

export default function Smoke() {
    const { gl, size, camera } = useThree()
    const pointsRef = useRef()

    // CPU-side particle data
    const particlesRef = useRef([])
    const nextEmitAtRef = useRef(0)

    // Texture
    const smokeTexture = useTexture('/textures/smoke.png', (t) => {
        t.flipY = false
        t.colorSpace = THREE.SRGBColorSpace
    })

    // Geometry + Material
    const { geometry, material } = useMemo(() => {
        const geom = new THREE.BufferGeometry()
        const positions = new Float32Array(SMOKE_CONFIG.maxParticles * 3)
        const particleData = new Float32Array(SMOKE_CONFIG.maxParticles * 2) // life, id

        geom.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(positions, 3)
        )
        geom.setAttribute(
            'particleData',
            new THREE.Float32BufferAttribute(particleData, 2)
        )

        geom.attributes.position.setUsage(THREE.DynamicDrawUsage)
        geom.attributes.particleData.setUsage(THREE.DynamicDrawUsage)
        geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1000)

        const mat = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                spinSpeed: { value: SMOKE_CONFIG.spinSpeed },
                map: { value: smokeTexture },
                sizeStart: { value: SMOKE_CONFIG.sizeStart },
                sizeEnd: { value: SMOKE_CONFIG.sizeEnd },
                fadeIn: { value: SMOKE_CONFIG.fadeIn },
                fadeOutStart: { value: SMOKE_CONFIG.fadeOutStart },
                colourStart: { value: SMOKE_CONFIG.colourStart },
                colourEnd: { value: SMOKE_CONFIG.colourEnd },
                uProjectionScale: { value: 1.0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.CustomBlending,
            blendEquation: THREE.AddEquation,
            blendSrc: THREE.OneFactor,
            blendDst: THREE.OneMinusSrcAlphaFactor,
        })

        return { geometry: geom, material: mat }
    }, [smokeTexture])

    // Helper: Spawn Particle
    const spawnParticle = () => {
        // Spherical spread
        const phi = Math.random() * Math.PI * 2
        const theta = Math.random() * Math.PI
        const r = Math.random() * SMOKE_CONFIG.spread

        const dir = new THREE.Vector3()
        dir.setFromSphericalCoords(r, theta, phi)

        // Upward-ish initial velocity with variance
        const velPhi = Math.random() * Math.PI * 2
        const velTheta = Math.random() * (Math.PI / 8)

        const vdir = new THREE.Vector3()
        vdir.setFromSphericalCoords(SMOKE_CONFIG.startSpeed, velTheta, velPhi)

        return {
            position: new THREE.Vector3().copy(SMOKE_CONFIG.position).add(dir),
            velocity: vdir,
            life: 0,
            maxLife: SMOKE_CONFIG.lifetime,
            id: Math.random(),
        }
    }

    // Reset emission timing on tab visibility change
    useEffect(() => {
        const onVis = () => {
            nextEmitAtRef.current = 0
            if (SMOKE_CONFIG.clearOnReturn) {
                particlesRef.current.length = 0
                geometry.setDrawRange(0, 0)
            }
        }
        document.addEventListener('visibilitychange', onVis)
        return () => document.removeEventListener('visibilitychange', onVis)
    }, [geometry])

    useFrame((state, rawDelta) => {
        // Clamp delta
        const delta = rawDelta > 0.25 ? 0.016 : rawDelta
        const particles = particlesRef.current

        // 1. Emission (Poisson process)
        nextEmitAtRef.current -= delta
        const mean = 1 / Math.max(1, SMOKE_CONFIG.emissionRate)

        while (
            nextEmitAtRef.current <= 0 &&
            particles.length < SMOKE_CONFIG.maxParticles
        ) {
            particles.push(spawnParticle())
            const u = Math.max(1e-6, Math.random())
            const interval = -Math.log(u) * mean
            nextEmitAtRef.current += Math.max(0.0005, interval)
        }

        // 2. Physics Update
        const target = SMOKE_CONFIG.attractorTarget
        const attractorRadiusSq =
            Math.max(0.001, SMOKE_CONFIG.attractorRadius) ** 2

        for (let i = 0; i < particles.length; i++) {
            const p = particles[i]
            p.life = Math.min(p.maxLife, p.life + delta)

            // Drag
            p.velocity.addScaledVector(p.velocity, -SMOKE_CONFIG.drag * delta)

            // Attractor (wind-like)
            const dir = new THREE.Vector3().subVectors(target, p.position)
            const distSq = dir.lengthSq()

            if (distSq > 1e-8) {
                const dist = Math.sqrt(distSq)
                dir.multiplyScalar(1 / dist) // normalize

                const k =
                    SMOKE_CONFIG.attractorIntensity /
                    (1 + distSq / attractorRadiusSq)
                p.velocity.addScaledVector(dir, k * delta)
            }

            // Integrate
            p.position.addScaledVector(p.velocity, delta)
        }

        // 3. Cull dead
        for (let i = particles.length - 1; i >= 0; i--) {
            if (particles[i].life >= particles[i].maxLife) {
                particles.splice(i, 1)
            }
        }

        // 4. Update Geometry
        const posArray = geometry.attributes.position.array
        const dataArray = geometry.attributes.particleData.array

        for (let i = 0; i < particles.length; i++) {
            const p = particles[i]
            const i3 = i * 3
            const i2 = i * 2

            posArray[i3] = p.position.x
            posArray[i3 + 1] = p.position.y
            posArray[i3 + 2] = p.position.z

            dataArray[i2] = p.life / p.maxLife
            dataArray[i2 + 1] = p.id
        }

        geometry.setDrawRange(0, particles.length)
        geometry.attributes.position.needsUpdate = true
        geometry.attributes.particleData.needsUpdate = true

        // 5. Update Uniforms
        material.uniforms.time.value = state.clock.elapsedTime

        // Pixel-correct point size
        const dpr = Math.min(gl.getPixelRatio(), 2)
        const fovRad = THREE.MathUtils.degToRad(camera.fov * 0.5)
        material.uniforms.uProjectionScale.value =
            (size.height * dpr) / (2 * Math.tan(fovRad))
    })

    return <points ref={pointsRef} geometry={geometry} material={material} />
}
